/**
 * Client form validation schema
 * Validates client data with country-specific rules
 */

import * as Yup from 'yup'
import { FORM_LABELS, LABELS } from '@constants'
import {
  createNLPostalCodeValidator,
  createNLKVKValidator,
  createNLBTWIDValidator,
  createEmailValidator,
} from '@utilities/validation'

// Explicit type for client form data
export interface IClientFormData {
  company_id: string
  country: {
    id: number
    name: string
    code: string
  }
  name: string
  code: string
  email: string
  street: string
  house_number: string
  house_number_addition: string
  postal_code: string
  city: string
  coc: string
  tax: string
  hourly_rate: number | null
  created_at: null
  updated_at: null
  deleted_at: null
  contacts: never[]
}

/**
 * Client validation schema with dynamic country-based validation
 * - Netherlands (NL): Strict validation for postal code, KVK, and BTW-ID
 * - Other countries: Basic format validation
 */
export const clientValidationSchema = () =>
  Yup.object().shape({
    company_id: Yup.string().required(FORM_LABELS.REQUIRED(LABELS.COMPANY)),

    country: Yup.object()
      .shape({
        id: Yup.number().required(),
        name: Yup.string().required(),
        code: Yup.string().required(),
      })
      .required(FORM_LABELS.REQUIRED(LABELS.COUNTRY)),

    name: Yup.string()
      .required(FORM_LABELS.REQUIRED(LABELS.CLIENT_NAME))
      .max(255, FORM_LABELS.MAX_LENGTH(LABELS.CLIENT_NAME, 255)),

    code: Yup.string().nullable().default('').max(255, FORM_LABELS.MAX_LENGTH(LABELS.CLIENT_CODE, 255)),

    email: createEmailValidator(FORM_LABELS.INVALID_EMAIL)
      .nullable()
      .default('')
      .max(255, FORM_LABELS.MAX_LENGTH(LABELS.EMAIL, 255)),

    street: Yup.string().required(FORM_LABELS.REQUIRED(LABELS.STREET)),

    house_number: Yup.string()
      .required(FORM_LABELS.REQUIRED(LABELS.HOUSE_NUMBER))
      .max(20, FORM_LABELS.MAX_LENGTH(LABELS.HOUSE_NUMBER, 20)),

    house_number_addition: Yup.string()
      .nullable()
      .max(20, FORM_LABELS.MAX_LENGTH(LABELS.HOUSE_NUMBER_ADDITION, 20)),

    // Postal code validation - conditional based on country
    postal_code: Yup.string()
      .required(FORM_LABELS.REQUIRED(LABELS.POSTAL_CODE))
      .when('country', {
        is: (country: any) => country?.code === 'NL',
        then: (schema) => createNLPostalCodeValidator(FORM_LABELS.INVALID_POSTAL_CODE_NL),
        otherwise: (schema) => schema.max(20, FORM_LABELS.MAX_LENGTH(LABELS.POSTAL_CODE, 20)),
      }),

    city: Yup.string().required(FORM_LABELS.REQUIRED(LABELS.CITY)),

    // KVK number validation - only for Netherlands
    coc: Yup.string()
      .default('')
      .when('country', {
        is: (country: any) => country?.code === 'NL',
        then: (schema) => createNLKVKValidator(FORM_LABELS.INVALID_KVK_NUMBER),
        otherwise: (schema) => schema.max(255, 'Maximaal 255 tekens'),
      }),

    // BTW-ID/VAT validation - conditional based on country
    tax: Yup.string()
      .default('')
      .when('country', {
        is: (country: any) => country?.code === 'NL',
        then: (schema) => createNLBTWIDValidator(FORM_LABELS.INVALID_BTW_ID_NL),
        otherwise: (schema) => schema.max(255, 'Maximaal 255 tekens'),
      }),

    hourly_rate: Yup.number()
      .nullable()
      .min(0, FORM_LABELS.HOURLY_RATE_NEGATIVE)
      .integer(FORM_LABELS.HOURLY_RATE_INTEGER),
  })
